import { useTranslation } from 'react-i18next';

/**
 * Helpers puros + hook de rótulo das telas de Análise do Financeiro interno.
 *
 * Origem: #76 — 2026-06-21 23:20 +0200 (Financeiro PR D). Separado do `.tsx` de componentes
 * para não disparar o aviso `react-refresh` (arquivo de componente não pode exportar
 * não-componentes).
 *
 * Usado por: `FinanceBalancePage`/`FinanceCashflowPage` (`dayNoonUtcMs`, `msToDateInput`),
 * `finance-entries/FinanceEntryEditorDialog.tsx` (`msToDateInput`),
 * `BillingSubscriptionsPage` (`dayBoundMs`), `financeAnalysisShared.tsx` (`useTagLabel`) —
 * web `apps/admin`.
 *
 * Dicas: há DUAS conversões de DatePicker → ms, e não são intercambiáveis: `dayBoundMs` é
 * início/fim do dia no fuso do navegador (janela dos recebíveis, #78) e `dayNoonUtcMs` é
 * meio-dia UTC (corte por dia no fuso da conta, #105). Trocar uma pela outra reabre a #105.
 */

/**
 * Rótulo de uma tag; `''` (sem tag) usa a string i18n "Sem tag".
 *
 * Origem: #76 — 2026-06-21 23:20 +0200 (Financeiro PR D).
 * Usado por: `TagBreakdownTable` (`financeAnalysisShared.tsx`).
 */
export function useTagLabel(): (tag: string) => string {
  const { t } = useTranslation();
  return (tag: string): string => (tag === '' ? t('admin.finance.analysis.noTag') : tag);
}

/**
 * Converte input de DatePicker (YYYY-MM-DD) para epoch ms (início/fim do dia, local).
 *
 * Origem: #76 — 2026-06-21 23:20 +0200 (Financeiro PR D).
 * Usado por: `routes/BillingSubscriptionsPage.tsx` (janela dos recebíveis, #78).
 * Dicas: NÃO serve para corte por dia do Financeiro — ali é `dayNoonUtcMs` (#105).
 */
export function dayBoundMs(value: string, end: boolean): number | undefined {
  if (value === '') return undefined;
  const [y, m, d] = value.split('-').map(Number);
  if (y === undefined || m === undefined || d === undefined) return undefined;
  return end
    ? new Date(y, m - 1, d, 23, 59, 59, 999).getTime()
    : new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
}

/**
 * Meio-dia UTC do dia civil escolhido no DatePicker (#105). Instante estável
 * para os cortes POR DIA do servidor (fuso da conta): fim-de-dia local do
 * navegador cairia no dia seguinte em contas a leste e incluiria lançamentos
 * de amanhã.
 *
 * Origem: #105 — 2026-07-02 17:10 +0200 (corte por dia no fuso da conta).
 * Usado por: `routes/FinanceCashflowPage.tsx`, `routes/FinanceBalancePage.tsx`.
 */
export function dayNoonUtcMs(value: string): number | undefined {
  if (value === '') return undefined;
  const [y, m, d] = value.split('-').map(Number);
  if (y === undefined || m === undefined || d === undefined) return undefined;
  return Date.UTC(y, m - 1, d, 12);
}

/**
 * YYYY-MM-DD (local) de um epoch ms — para popular DatePicker.
 *
 * Origem: #76 — 2026-06-21 23:20 +0200 (Financeiro PR D).
 * Usado por: `FinanceCashflowPage`, `FinanceBalancePage`, `FinanceEntryEditorDialog`.
 */
export function msToDateInput(ms: number): string {
  const d = new Date(ms);
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${String(d.getFullYear())}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
